import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useLanguage } from '../context/LanguageContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3024c52e-4cee-4083-aea4-e49427d8ac34/artifacts/9fg9a2xl_13921DDF-6D23-4936-90D9-EEE3C5FB5A93.png";

export const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.services'), path: '/services' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="navbar-glass sticky top-0 z-50 w-full" data-testid="navbar">
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Gomez Cleaning Service LLC" 
              className="h-24 md:h-32 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-base ${isActive(link.path) ? 'active' : ''}`}
                data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            <LanguageSwitcher />
            <a href="tel:+13233780061" className="flex items-center gap-2 text-[#1E88E5] font-semibold">
              <Phone size={18} />
              <span>(323) 378-0061</span>
            </a>
            <Link to="/contact">
              <Button 
                className="btn-primary"
                data-testid="nav-quote-btn"
              >
                {t('nav.getQuote')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-[#334155]"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-[#E2E8F0] animate-fadeIn" data-testid="mobile-menu">
          <div className="container mx-auto px-6 py-6">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link text-lg py-2 ${isActive(link.path) ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                  data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-[#E2E8F0]">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-[#334155]">{t('footer.language')}:</span>
                  <LanguageSwitcher />
                </div>
                <a 
                  href="tel:+13233780061" 
                  className="flex items-center gap-2 text-[#1E88E5] font-semibold text-lg mb-4"
                >
                  <Phone size={20} />
                  <span>(323) 378-0061</span>
                </a>
                <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                  <Button className="btn-primary w-full" data-testid="mobile-quote-btn">
                    {t('nav.getQuote')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
